import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, DatePicker, Form, InputNumber, message, Space, Typography, Popconfirm, Badge, Avatar, Flex, Grid, Progress, Tag } from 'antd';
import { DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getHistoricalImports, createHistoricalImport, deleteHistoricalImport } from '../../ChannelsServices';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { Resource, Action } from '../../../../Constants/socket';
import useSocketEvent from '../../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../../Hooks/useSocketSubscription';
import useSocket from '../../../../Hooks/useSocket';
import { resolveBackendMessage } from '../../../../Utils/apiMessages';
import HistoricalImportModal from './HistoricalImportModal';
import AppTable from '../../../Shared/AppTable/AppTable';

const { Text } = Typography;
const { useBreakpoint } = Grid;

/**
 * Extracts the terminal status keyword from a backend status object or string.
 * Used only for status logic (badge colors, processing detection).
 * For localized display, use resolveBackendMessage(t, status) directly.
 *
 * @param {Object|string} status - The status value from the API.
 * @returns {string} The extracted status keyword (e.g. 'completed', 'failed', 'pending').
 */
const extractStatusKey = (status) => {
    if (typeof status === 'string') return status;
    if (status && typeof status === 'object' && status.message_code) {
        const parts = status.message_code.split('.');
        return parts[parts.length - 1];
    }
    return 'unknown';
};

/**
 * HistoricalImportsTab component displays a paginated table of historical imports for a channel.
 * Subscribes to real-time socket events for live progress tracking, consistent with
 * the DataExportList pattern.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.channel - The channel object containing channel details.
 * @returns {JSX.Element} The rendered HistoricalImportsTab component.
 */
const HistoricalImportsTab = ({ channel }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = useBreakpoint() || {};
    const { handleError } = useErrorHandler();
    const { socket } = useSocket();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [importing, setImporting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();

    // --- Socket subscriptions ---
    // Subscribe to individual import IDs for UPDATE events
    const importIds = useMemo(() => data.map(item => item.id), [data]);
    useSocketSubscription(Resource.CHANNEL_HISTORICAL_IMPORT, importIds);

    // Handle real-time UPDATE events (progress, status, summary changes)
    const handleImportUpdate = useCallback((updatedImport) => {
        setData(prevData => prevData.map(item => {
            if (item.id === updatedImport.id) {
                return {
                    ...item,
                    ...updatedImport,
                    status: {
                        ...(item.status || {}),
                        message_code: updatedImport.status_message_code || item.status?.message_code,
                        message_data: updatedImport.status_message_data || item.status?.message_data,
                    },
                    progress: updatedImport.progress !== undefined ? updatedImport.progress : item.progress,
                    summary: updatedImport.summary !== undefined ? updatedImport.summary : item.summary,
                };
            }
            return item;
        }));
    }, []);

    // Handle CREATE events — refetch the list to pick up the new import
    const handleImportCreate = useCallback((data) => {
        fetchImports(pagination.current, pagination.pageSize);

        // Subscribe to new import from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.CHANNEL_HISTORICAL_IMPORT, [data.id], data.created_at);
        }
    }, [pagination.current, pagination.pageSize, socket]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle DELETE events — refetch the current page to maintain correct pagination
    const handleImportDelete = useCallback(() => {
        fetchImports(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    useSocketEvent(Resource.CHANNEL_HISTORICAL_IMPORT, Action.UPDATE, handleImportUpdate);
    useSocketEvent(Resource.CHANNEL_HISTORICAL_IMPORT, Action.CREATE, handleImportCreate);
    useSocketEvent(Resource.CHANNEL_HISTORICAL_IMPORT, Action.DELETE, handleImportDelete);

    // --- Data fetching ---

    const fetchImports = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: pageSize,
                sort_by: 'created_at',
                sort_direction: 'desc'
            };
            const response = await getHistoricalImports(channel.id, params);
            setData(response?.data);
            setPagination({
                current: response.metadata?.page || page,
                pageSize: response.metadata?.page_size || pageSize,
                total: response.metadata?.total || 0
            });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [channel.id, handleError]);

    useEffect(() => {
        if (channel?.id) {
            fetchImports(pagination.current, pagination.pageSize);
        }
    }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTableChange = useCallback((newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
        fetchImports(newPagination.current, newPagination.pageSize);
    }, [fetchImports]);

    // --- Modal handlers ---

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleImportSuccess = useCallback(() => {
        fetchImports(1, pagination.pageSize);
    }, [fetchImports, pagination.pageSize]);

    const handleDelete = useCallback(async (id) => {
        try {
            await deleteHistoricalImport(id);
            message.success(t('import_deleted_successfully'));
            fetchImports(pagination.current, pagination.pageSize);
        } catch (error) {
            handleError(error);
        }
    }, [t, pagination.current, pagination.pageSize, fetchImports, handleError]);

    // --- Rendering helpers ---

    const avatarStyle = useMemo(() => ({
        backgroundColor: isDarkMode ? '#1f1f1f' : '#f0f0f0',
        color: isDarkMode ? '#8c8c8c' : '#595959'
    }), [isDarkMode]);

    const getStatusBadge = useCallback((status) => {
        const statusKey = extractStatusKey(status);
        // Use resolveBackendMessage to translate message_code as-is
        const statusObj = typeof status === 'object' ? status : null;
        const localizedText = statusObj ? resolveBackendMessage(t, statusObj) : null;

        const colorMap = {
            completed: 'success',
            failed: 'error',
            timeout: 'error',
            pending: 'processing',
            request_received: 'processing',
            in_progress: 'processing',
            processing: 'processing',
        };
        const color = colorMap[statusKey] || 'default';
        return <Badge status={color} text={localizedText || statusKey || '-'} />;
    }, [t]);

    const renderProgress = useCallback((progress, record) => {
        const statusKey = extractStatusKey(record.status);
        const isProcessing = statusKey === 'in_progress' || statusKey === 'processing';
        const isCompleted = statusKey === 'completed';
        const isFailed = statusKey === 'failed' || statusKey === 'timeout';

        if (isCompleted) {
            return <Text style={{ color: '#52c41a', fontWeight: 600 }}>100%</Text>;
        }
        if (isFailed) {
            return <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>{Math.round(progress || 0)}%</Text>;
        }
        if (isProcessing && progress != null) {
            return <Text style={{ color: '#1677ff', fontWeight: 600 }}>{Math.round(progress)}%</Text>;
        }
        return <Text type="secondary">—</Text>;
    }, []);

    const columns = useMemo(() => [
        {
            title: t('file_name'),
            key: 'file_name',
            render: (_, record) => (
                <Space>
                    <Avatar
                        icon={<HistoryOutlined />}
                        style={avatarStyle}
                    />
                    <Text>{record.id}</Text>
                </Space>
            ),
        },
        {
            title: t('progress'),
            dataIndex: 'progress',
            key: 'progress',
            render: renderProgress,
            width: 100,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: getStatusBadge,
            width: 120,
        },
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => date ? (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(date), 'PPP p')}
                </span>
            ) : '-',
            responsive: ['md'],
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Popconfirm
                    title={t('delete_import_confirmation')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('yes')}
                    cancelText={t('no')}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ], [t, avatarStyle, handleDelete, getStatusBadge, renderProgress]);

    /**
     * Expanded row renderer showing live import summary with progress bars,
     * following the DataExportList expandedRowRender pattern.
     */
    const expandedRowRender = useCallback((record) => {
        const summary = record.summary;
        const statusKey = extractStatusKey(record.status);
        const statusObj = typeof record.status === 'object' ? record.status : null;

        const isProcessing = statusKey === 'in_progress' || statusKey === 'processing';
        const isCompleted = statusKey === 'completed';
        const isFailed = statusKey === 'failed' || statusKey === 'timeout';

        let tagColor = 'default';
        if (isCompleted) tagColor = 'success';
        else if (isFailed) tagColor = 'error';
        else if (isProcessing) tagColor = 'processing';

        return (
            <div style={{ padding: '12px 48px 4px' }}>
                {/* Status */}
                <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{t('status')}:</Text>
                    <Tag color={tagColor} style={{ margin: 0 }}>
                        {statusObj
                            ? (resolveBackendMessage(t, statusObj) || statusKey)
                            : statusKey}
                    </Tag>
                </Flex>

                {/* Timestamps */}
                <Flex wrap="wrap" gap="large" style={{ fontSize: 12, marginBottom: 12 }}>
                    {record.started_at && (
                        <span style={{ color: '#595959' }}>
                            {t('started_at')}: <Text strong>{format(new Date(record.started_at), 'PPP p')}</Text>
                        </span>
                    )}
                    {record.completed_at && (
                        <span style={{ color: '#52c41a' }}>
                            {t('completed_at')}: <Text strong style={{ color: '#52c41a' }}>{format(new Date(record.completed_at), 'PPP p')}</Text>
                        </span>
                    )}
                    {record.failed_at && (
                        <span style={{ color: '#ff4d4f' }}>
                            {t('failed_at')}: <Text strong style={{ color: '#ff4d4f' }}>{format(new Date(record.failed_at), 'PPP p')}</Text>
                        </span>
                    )}
                </Flex>

                {/* Global progress bar */}
                {record.progress != null && (
                    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                        <Progress
                            percent={Math.round(record.progress || 0)}
                            strokeColor={Math.round(record.progress || 0) >= 100 ? '#52c41a' : '#1677ff'}
                            size={[null, 8]}
                        />
                    </div>
                )}

                {/* Summary metrics */}
                {summary && (
                    <Flex wrap="wrap" gap="large" style={{ fontSize: 12, paddingBottom: 8 }}>
                        <span style={{ color: '#595959' }}>
                            {t('total_conversations')}: <Text strong>{(summary.total_conversations || 0).toLocaleString()}</Text>
                        </span>
                        <span style={{ color: '#52c41a' }}>
                            {t('conversations_synced')}: <Text strong style={{ color: '#52c41a' }}>{(summary.conversations_synced || 0).toLocaleString()}</Text>
                        </span>
                        <span style={{ color: (summary.conversations_failed || 0) > 0 ? '#ff4d4f' : '#bfbfbf' }}>
                            {t('conversations_failed')}: <Text strong style={{ color: (summary.conversations_failed || 0) > 0 ? '#ff4d4f' : '#bfbfbf' }}>{(summary.conversations_failed || 0).toLocaleString()}</Text>
                        </span>
                        <span style={{ color: '#52c41a' }}>
                            {t('messages_synced')}: <Text strong style={{ color: '#52c41a' }}>{(summary.messages_synced || 0).toLocaleString()}</Text>
                        </span>
                        <span style={{ color: (summary.messages_failed || 0) > 0 ? '#ff4d4f' : '#bfbfbf' }}>
                            {t('messages_failed')}: <Text strong style={{ color: (summary.messages_failed || 0) > 0 ? '#ff4d4f' : '#bfbfbf' }}>{(summary.messages_failed || 0).toLocaleString()}</Text>
                        </span>
                    </Flex>
                )}
            </div>
        );
    }, [t]);

    // UI render

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Flex
                gap="middle"
                wrap="wrap"
                align="center"
                justify="flex-end"
                style={{
                    width: '100%',
                    padding: '8px 0 4px'
                }}
            >
                <Button
                    type="primary"
                    icon={<HistoryOutlined />}
                    onClick={handleOpenModal}
                >
                    {t('start_historical_import')}
                </Button>
            </Flex>

            <div style={{ width: '100%' }}>
                <AppTable
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    scroll={{ x: 600 }}
                    loading={loading}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => record.progress != null || !!record.summary,
                    }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: !screens.xs,
                    }}
                    onChange={handleTableChange}
                    locale={{
                        emptyText: (
                            <div style={{ padding: '32px 0', textAlign: 'center' }}>
                                <HistoryOutlined style={{ fontSize: '32px', color: '#8c8c8c', marginBottom: '16px' }} />
                                <Typography.Title level={5} type="secondary">{t('no_historical_imports')}</Typography.Title>
                                <Typography.Text type="secondary">{t('start_sync_description')}</Typography.Text>
                            </div>
                        )
                    }}
                />
            </div>

            <HistoricalImportModal
                open={isModalOpen}
                channelId={channel?.id}
                onCancel={handleCloseModal}
                onSuccess={handleImportSuccess}
            />
        </Space>
    );
};

export default HistoricalImportsTab;
